import { useContext, useState } from 'react'

import { useClassNames } from '@factorialco/deprecated-design-system'
import { Reorder } from 'framer-motion'
import { compact } from 'lodash'

import { observer } from 'mobx-react'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMemo, useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { handleResult } from 'modules/core/lib/handleMutation'

import type { FieldType } from 'modules/reports/components/Editor/Field/types'
import { reportStoreContext } from 'modules/reports/contexts/reportStoreContext'
import { useValidationContext } from 'modules/reports/contexts/validationContext'
import useValidateProjections from 'modules/reports/hooks/useValidateProjection'

import * as styles from './index.vanilla.css'

type SortableItemProps = {
  item: FieldType
  renderItem: (item: FieldType, disabled: boolean, loading: boolean) => JSX.Element
  disabled: boolean
  loading: boolean
  onDragEnd: () => void
}

const SortableItem = observer(
  ({ item, renderItem, disabled, onDragEnd, loading }: SortableItemProps) => {
    const notClickable = disabled || loading

    return (
      <Reorder.Item
        onDragEnd={onDragEnd}
        value={item.field?.attributes.path}
        className={notClickable ? styles.disabledItem : styles.item}
        dragListener={!notClickable}
      >
        {renderItem(item, disabled, loading)}
      </Reorder.Item>
    )
  }
)

interface Props<T> {
  items: T[]
  renderItem: (item: T, disabled: boolean, loading: boolean) => JSX.Element
  onSortEnd: (items: T[]) => void
}

const EMPTY_ARRAY: Array<string> = []

/**
 * Variation of SortableBoxList, that avoids changing the first field table
 *
 * Some extra things were also removed, like the empty state.
 */
function SortableList({ items, renderItem, onSortEnd }: Props<FieldType>) {
  const store = useContext(reportStoreContext)
  const [previousItems, setPreviousItems] = useState(items)
  const [firstItemDisabled, setFirstItemDisabled] = useState(false)
  const [randomKey, setRandomKey] = useState(0)
  const [isLoading, setLoading] = useState(false)
  const [, setValidation] = useValidationContext()
  const { validateProjection } = useValidateProjections()

  const cx = useClassNames(styles)

  const projections = useMemo(
    () => store?.projection.paths() || EMPTY_ARRAY,
    // FIXME!: Unable to get a stable reference of Mobx FormFields
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [store?.projection.paths().toString()]
  )
  const filters = useUnsafeMemo(
    () => store?.filters.paths() || EMPTY_ARRAY,
    // FIXME!: Unable to get a stable reference of Mobx FormFields
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [store?.filters.paths().toString()]
  )

  const validate = async () => {
    setLoading(true)

    const result = handleResult(await validateProjection({ projections, filters }), () => undefined)

    if (result.success && result.data.validateProjection?.isValid) {
      setValidation(result.data.validateProjection)
      setRandomKey((lastRandomKey) => lastRandomKey + 1)
    } else {
      onSortEnd(previousItems)
    }

    setLoading(false)
  }

  const handleReorder = useUnsafeCallback(
    (newItemPaths: string[]) => {
      setPreviousItems(items)

      const newItems = compact(
        newItemPaths.map((path) =>
          items.find((item) => item.field && item.field?.attributes.path === path)
        )
      )

      onSortEnd(newItems)
    },
    [items, onSortEnd]
  )

  useUnsafeEffect(() => {
    ;(async () => {
      if (!filters.length) return

      setLoading(true)

      const result = handleResult(
        await validateProjection({ projections: projections.slice(1), filters }),
        () => undefined
      )

      if (result.success && result.data.validateProjection) {
        setFirstItemDisabled(!result.data.validateProjection.isValid)
      }

      setLoading(false)
    })()
  }, [filters, projections, validateProjection])

  return (
    <Reorder.Group
      key={randomKey}
      axis="y"
      values={items.map((item) => item.field?.attributes.path)}
      onReorder={handleReorder}
      className={cx('list', { isLoading })}
    >
      {items.map((item, index) => (
        <SortableItem
          onDragEnd={validate}
          key={item.field?.attributes.path}
          item={item}
          loading={isLoading}
          renderItem={renderItem}
          disabled={index === 0 && firstItemDisabled}
        />
      ))}
    </Reorder.Group>
  )
}

export default observer(SortableList)
